import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import MySelect from './MySelect'
import citiesData from '../../src/data/cities.json'

interface AbsoluteTopBarFilterProps {
  isVisible: boolean
  onClose?: () => void
}

interface SearchFilters {
  imLookingFor: string[]
  orientation: string[]
  iLiveIn: string[]
  distance: string[]
}

interface Option {
  value: string
  label: string
  latitude?: number
  longitude?: number
}

const DEFAULT_FILTERS: SearchFilters = {
  imLookingFor: [],
  orientation: [],
  iLiveIn: [],
  distance: [],
}

const STORAGE_KEY = 'searchFilters'

export default function AbsoluteTopBarFilter({
  isVisible,
}: AbsoluteTopBarFilterProps) {
  const { t } = useTranslation()
  const location = useLocation()

  const [filters, setFilters] = useState<SearchFilters>(() => {
    try {
      const savedFilters = localStorage.getItem(STORAGE_KEY)
      if (savedFilters) {
        return JSON.parse(savedFilters)
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error)
    }
    return DEFAULT_FILTERS
  })

  const [locationOptions, setLocationOptions] = useState<Option[]>([])

  useEffect(() => {
    const options = citiesData.flatMap(province =>
      province.cities.map(city => ({
        value: city.city_fr,
        label: `${city.city_fr} (${city.postal_code})`,
        latitude: city.latitude,
        longitude: city.longitude,
      })),
    )
    setLocationOptions(options)
  }, [])

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters))
  }, [filters])

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: Array.isArray(value) ? value : [value],
    }))
  }

  const handleReset = (key: keyof SearchFilters) => {
    setFilters(prev => ({
      ...prev,
      [key]: [],
    }))
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()

    const timestamp = new Date().getTime()
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('timestamp', timestamp.toString())

    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        searchParams.set(key, value.join(','))
      }
    })

    window.location.href = `/?${searchParams.toString()}`
  }

  const filterOptions = {
    imLookingFor: [
      { value: 'woman', label: t('create-ad.woman') },
      { value: 'man', label: t('create-ad.man') },
      { value: 'couple', label: t('create-ad.couple') },
      { value: 'transgender', label: t('create-ad.transgender') },
    ],
    orientation: [
      { value: 'heterosexual', label: t('create-ad.heterosexual') },
      { value: 'bisexual', label: t('create-ad.bi') },
      { value: 'homosexual', label: t('create-ad.homosexual') },
    ],
    distance: [
      { value: '< 10km', label: '< 10km' },
      { value: '< 20km', label: '< 20km' },
      { value: '< 50km', label: '< 50km' },
      { value: '< 75km', label: '< 75km' },
      { value: '< 100km', label: '< 100km' },
    ],
  }

  return (
    <div
      className={`flex-grow filter-top p-0 m-0 ${
        isVisible ? 'block' : 'hidden'
      } md:block`}
    >
      <form className='w-full flex-grow p-0 m-0' onSubmit={handleSearch}>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-0 m-0'>
          <div className='col-span-1 h-40px lg:pr-5 mb-1 md:mb-0'>
            <label
              htmlFor='imLookingForSelect'
              className='text-center uppercase text-xl mb-2 hidden lg:block'
            >
              {t('im-looking-for')}
            </label>
            <MySelect
              id='imLookingForSelect'
              label={t('im-looking-for')}
              variant='mobile'
              options={filterOptions.imLookingFor}
              value={filters.imLookingFor.join(',')}
              onChange={value => handleFilterChange('imLookingFor', value)}
              handleReset={() => handleReset('imLookingFor')}
              hideLabel={window.innerWidth > 768}
              showNonMatchingOptions={true}
              className='flex-grow w-full cursor-pointer h-40px text-sm rounded-md outline-none ring-0 focus:ring-0 text-anthracite font-semibold'
            />
          </div>

          <div className='col-span-1 h-40px lg:pr-5 mb-1 md:mb-0'>
            <label
              htmlFor='orientationSelect'
              className='text-center uppercase text-xl mb-2 hidden lg:block'
            >
              {t('orientation')}
            </label>
            <MySelect
              id='orientationSelect'
              label={t('orientation')}
              variant='mobile'
              options={filterOptions.orientation}
              value={filters.orientation.join(',')}
              onChange={value => handleFilterChange('orientation', value)}
              handleReset={() => handleReset('orientation')}
              hideLabel={window.innerWidth > 768}
              showNonMatchingOptions={true}
              className='flex-grow w-full cursor-pointer h-40px text-sm rounded-md outline-none ring-0 focus:ring-0 text-anthracite font-semibold'
            />
          </div>

          <div className='col-span-1 h-40px lg:pr-5 mb-1 md:mb-0'>
            <label
              htmlFor='iLiveInSelect'
              className='text-center uppercase text-xl mb-2 hidden lg:block'
            >
              {t('i-live-in')}
            </label>
            <MySelect
              id='iLiveInSelect'
              label={t('i-live-in')}
              variant='mobile'
              options={locationOptions}
              value={filters.iLiveIn.join(',')}
              onChange={value => handleFilterChange('iLiveIn', value)}
              handleReset={() => handleReset('iLiveIn')}
              hideLabel={window.innerWidth > 768}
              showNonMatchingOptions={false}
              className='flex-grow w-full cursor-pointer h-40px text-sm rounded-md outline-none ring-0 focus:ring-0 text-anthracite font-semibold'
            />
          </div>

          <div className='col-span-1 h-40px lg:pr-5 mb-1 md:mb-0'>
            <label
              htmlFor='distanceSelect'
              className='text-center uppercase text-xl mb-2 hidden lg:block'
            >
              {t('distance')}
            </label>
            <MySelect
              id='distanceSelect'
              label={t('distance')}
              variant='mobile'
              options={filterOptions.distance}
              value={filters.distance.join(',')}
              onChange={value => handleFilterChange('distance', value)}
              handleReset={() => handleReset('distance')}
              hideLabel={window.innerWidth > 768}
              showNonMatchingOptions={true}
              className='flex-grow w-full cursor-pointer h-40px text-sm rounded-md outline-none ring-0 focus:ring-0 text-anthracite font-semibold'
            />
          </div>

          <div className='col-span-1 md:col-span-2 lg:col-span-1'>
            <div className='flex flex-col h-full justify-end'>
              <button
                type='submit'
                className='mt-0 lg:mt-9 w-full bg-redpink hover:text-anthracite transition-all text-white font-lightbold h-40px px-4 rounded-md'
              >
                {t('search')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
